import React, { useState, useEffect } from "react";
import { useAuth } from "../utils/useAuth";
import { db } from "../firebase";
import { doc, getDoc, getDocs, collection, query, where, onSnapshot } from "firebase/firestore";

export default function RefereeMatchFees() {
  const { user, profile } = useAuth();

  const [completedGames, setCompletedGames] = useState([]);
  const [refereeMatchFees, setRefereeMatchFees] = useState({});
  const [defaultRates, setDefaultRates] = useState({});

  // Load completed games where this referee was assigned
  useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, "games"),
      where("status", "==", "completed")
    );

    const unsub = onSnapshot(q, (snap) => {
      const allGames = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      
      // Filter games where this referee was involved in any role
      const myGames = allGames.filter(g => 
        g.assignedRefereeId === user.uid ||
        g.assistantReferee1Id === user.uid ||
        g.assistantReferee2Id === user.uid ||
        g.assistant4Id === user.uid ||
        g.assistant5Id === user.uid ||
        g.timeKeeperId === user.uid ||
        g.playerMovementRecorderId === user.uid ||
        g.statisticianId === user.uid ||
        g.technicalZoneManagerId === user.uid ||
        g.pointOfOrigin1Id === user.uid ||
        g.pointOfOrigin2Id === user.uid
      );

      setCompletedGames(myGames);
    });

    return unsub;
  }, [user]);

  // Load billing rates
  useEffect(() => {
    async function loadRates() {
      if (!profile?.union) return;

      try {
        // Get all billing rates documents and find one that matches the referee's union
        const ratesSnap = await getDocs(collection(db, "billingRates"));
        
        // Try to find rates from a manager in the same union
        let foundRates = null;
        for (const rateDoc of ratesSnap.docs) {
          const managerId = rateDoc.id;
          const managerDoc = await getDoc(doc(db, "users", managerId));
          
          if (managerDoc.exists() && managerDoc.data().union === profile.union) {
            foundRates = rateDoc.data();
            break;
          }
        }

        // If no union match, use first available rates
        if (!foundRates && ratesSnap.docs.length > 0) {
          foundRates = ratesSnap.docs[0].data();
        }

        if (foundRates) {
          setDefaultRates({
            referee: foundRates.referee || 0,
            assistant1: foundRates.assistant1 || 0,
            assistant2: foundRates.assistant2 || 0,
            assistant4: foundRates.assistant4 || 0,
            assistant5: foundRates.assistant5 || 0,
            timekeeper: foundRates.timekeeper || 0,
            playermovement: foundRates.playermovement || 0,
            statistician: foundRates.statistician || 0,
            technicalzone: foundRates.technicalzone || 0,
            pointoforigin1: foundRates.pointoforigin1 || 0,
            pointoforigin2: foundRates.pointoforigin2 || 0,
          });
          setRefereeMatchFees(foundRates.refereeMatchFees || {});
        }
      } catch (err) {
        console.error("Error loading rates:", err);
      }
    }
    
    loadRates();
  }, [profile]);

  // Determine role and get billing rate for a game
  function getGameDetails(game) {
    let role = "";
    let roleKey = "";
    
    if (game.assignedRefereeId === user.uid) {
      role = "Referee";
      roleKey = "referee";
    } else if (game.assistantReferee1Id === user.uid) {
      role = "AR1";
      roleKey = "assistant1";
    } else if (game.assistantReferee2Id === user.uid) {
      role = "AR2";
      roleKey = "assistant2";
    } else if (game.assistant4Id === user.uid) {
      role = "A4";
      roleKey = "assistant4";
    } else if (game.assistant5Id === user.uid) {
      role = "A5";
      roleKey = "assistant5";
    } else if (game.timeKeeperId === user.uid) {
      role = "Timekeeper";
      roleKey = "timekeeper";
    } else if (game.playerMovementRecorderId === user.uid) {
      role = "PMR";
      roleKey = "playermovement";
    } else if (game.statisticianId === user.uid) {
      role = "Statistician";
      roleKey = "statistician";
    } else if (game.technicalZoneManagerId === user.uid) {
      role = "TZM";
      roleKey = "technicalzone";
    } else if (game.pointOfOrigin1Id === user.uid) {
      role = "PO1";
      roleKey = "pointoforigin1";
    } else if (game.pointOfOrigin2Id === user.uid) {
      role = "PO2";
      roleKey = "pointoforigin2";
    }

    // Get rate: competition-specific first, then default
    let rate = 0;
    if (game.competition && refereeMatchFees[game.competition]?.[roleKey]) {
      rate = refereeMatchFees[game.competition][roleKey];
    } else {
      rate = defaultRates[roleKey] || 0;
    }

    return { role, rate };
  }

  // Calculate total earnings
  const totalEarnings = completedGames.reduce((sum, game) => {
    const { rate } = getGameDetails(game);
    return sum + parseFloat(rate || 0);
  }, 0);

  return (
    <div className="max-w-6xl mx-auto p-6">
      <h2 className="text-2xl font-bold mb-6">Match Fees</h2>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-6 rounded-lg shadow">
          <div className="text-sm font-semibold mb-1">Total Games Completed</div>
          <div className="text-4xl font-bold">{completedGames.length}</div>
        </div>
        
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-lg shadow">
          <div className="text-sm font-semibold mb-1">Total Earnings</div>
          <div className="text-4xl font-bold">R {totalEarnings.toFixed(2)}</div>
        </div>
      </div>

      {/* Games Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 bg-gray-100 border-b">
          <h3 className="font-bold text-lg">Completed Games</h3>
        </div>
        
        {completedGames.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            No completed games yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Game</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Competition</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Role</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Earnings</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {completedGames.map((game) => {
                  const { role, rate } = getGameDetails(game);
                  return (
                    <tr key={game.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">{game.date}</td>
                      <td className="px-4 py-3 text-sm">
                        {game.teamA} vs {game.teamB}
                      </td>
                      <td className="px-4 py-3 text-sm">{game.competition || "N/A"}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded">
                          {role}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-semibold">
                        R {parseFloat(rate || 0).toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-gray-50 border-t">
                <tr>
                  <td colSpan="4" className="px-4 py-3 text-right font-bold">
                    Total:
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-lg">
                    R {totalEarnings.toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
